import { useTheme } from "@mui/material";
import type { ReactNode } from "react";
import { usePortfolioQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioQuery";
import { usePortfolioRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import type {
	ClientPortfolioDomainEnum,
	PortfolioDetailResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { useParsedParam } from "web_myplann/Shared/Hooks/useParsedParam";
import { useRedirectGuard } from "web_myplann/Shared/Hooks/useRedirectGuard";
import { parseToFiniteNumber } from "web_myplann/Shared/Utils/parsers";

type Props = {
	expectedDomain: ClientPortfolioDomainEnum;
	title: string;
	children: (ctx: {
		portfolio: PortfolioDetailResponseDTO;
		portfolioId: number;
		detailRoute: string;
	}) => ReactNode;
};

export const PortfolioEditLayout = ({
	expectedDomain,
	title,
	children,
}: Props) => {
	const theme = useTheme();
	const portfolioId = useParsedParam("id", parseToFiniteNumber);

	const { data: portfolio, isLoading } = usePortfolioQuery(portfolioId);

	const detailRoute =
		portfolioId !== undefined
			? Routing.Portfolio.PortfolioDetail.path(String(portfolioId))
			: Routing.Portfolio.PortfolioList.path();

	const { hasRight: hasPortfolioRight, isPending: isRightPending } =
		usePortfolioRightOptional();
	useRedirectGuard(!isRightPending && !hasPortfolioRight, detailRoute);

	const isEditable =
		portfolio !== undefined &&
		!portfolio.archived &&
		portfolio.domain === expectedDomain;
	useRedirectGuard(portfolio !== undefined && !isEditable, detailRoute);

	const canRenderChildren = isEditable && portfolioId !== undefined;

	return (
		<MyPlannLayout
			bgColor={theme.palette.grey[50]}
			disableContentPadding
			topBarBgColor={theme.palette.background.paper}
			topBar={
				<NavigationHeader
					title={title}
					bgColor={theme.palette.background.paper}
					defaultRoute={detailRoute}
					replaceOnBack
					backIconColor={theme.palette.grey[900]}
					backIconSize={theme.iconSize.md}
				/>
			}
		>
			{isLoading ? (
				<DetailSectionLoader />
			) : canRenderChildren ? (
				children({
					portfolio,
					portfolioId,
					detailRoute,
				})
			) : null}
		</MyPlannLayout>
	);
};
